import { i18n } from "@lingui/core";
import WarpElement from "@warp-ds/elements-core";
import "@warp-ds/elements/components/card";
import { css, html, nothing } from "lit";
import type { NewCarLatestModelItem } from "../../models/newCarSections.js";
import { activateI18n } from "../../utils/i18n.js";
import { arrowKeyAction, edgeState, pageDistance, slideStride } from "../carousel-paging.js";
import { trackClickEvent } from "../tracking.js";

const HEADING_ID = "newcar-latest-models-heading";

// Whole literals, not fragments: the Warp plugin generates this component's styles by scanning the
// file's raw source text, so an assembled class never gets emitted.
const ARROW_BASE = "flex items-center justify-center w-48 h-48 rounded-full s-surface-elevated-200 shadow-m hover:shadow-l transition";
// Fades out at the ends (`transition` animates opacity); #keepFocusVisible moves focus off it.
const ARROW_DISABLED = "opacity-0 pointer-events-none";

class NewCarLatestModels extends WarpElement {
    declare header: string;
    declare items: Array<NewCarLatestModelItem>;
    declare canPrev: boolean;
    declare canNext: boolean;

    static properties = {
        header: { type: String },
        items: { type: Array },
        canPrev: { state: true },
        canNext: { state: true },
    };

    static styles = [
        WarpElement.styles,
        css`
            :host {
                display: block;
            }
            .carousel-track {
                scrollbar-width: none;
                scroll-snap-type: x mandatory;
                /* The track is padded so card focus rings aren't clipped by overflow-x-auto.
                   Without matching scroll padding the first card snaps 12px out of position and
                   the ring it protects gets cut off anyway. */
                scroll-padding-inline-start: 12px;
            }
            .carousel-track::-webkit-scrollbar {
                display: none;
            }
            .carousel-track > * {
                scroll-snap-align: start;
            }
            /* The cards are lazy-loaded, so the box has to be right before the bytes arrive —
               otherwise the row resizes mid-scroll, which is exactly when the off-screen images
               start loading. The width/height attributes reserve it; this non-auto aspect-ratio
               also keeps it, because an auto ratio hands control back to the intrinsic one on load
               and would shift the layout again the day the CDN returns something other than 2:1. */
            .card-image {
                aspect-ratio: 2 / 1;
                object-fit: cover;
            }
            /* Keep the placeholder LAST. The Warp plugin swaps the token but leaves the trailing
               semicolon, and a stray top-level ";" is parsed as the start of a qualified rule —
               it swallows the selector of whatever rule comes next, silently dropping it. */
            @css-placeholder---qIKG;
        `,
    ];

    #resizeObserver?: ResizeObserver;
    #rafHandle: number | null = null;

    constructor() {
        super();
        this.header = "";
        this.items = [];
        // The server cannot measure overflow, so both arrows start down and #measure reveals them
        // only once the track is laid out. Guessing from the item count flashes a next arrow in
        // and straight back out when the cards already fit.
        this.canPrev = false;
        this.canNext = false;
    }

    connectedCallback() {
        activateI18n();
        super.connectedCallback();
    }

    firstUpdated() {
        const track = this.#track;
        if (!track) return;
        // The New Car tab panel renders with `hidden` unless opened on ?tab=newcar, and a hidden
        // track measures 0 for everything. Observing it catches the 0 -> N transition when the tab
        // is shown, which no resize event fires for — and subsumes resize, so there is no listener.
        this.#resizeObserver = new ResizeObserver(() => this.#measure());
        this.#resizeObserver.observe(track);
        this.#measure();
    }

    updated(changed: Map<string, unknown>) {
        // ResizeObserver watches the track's own border box, which the wrapper pins — swapping the
        // item list changes scrollWidth without resizing that box, so no callback fires and the
        // arrows would keep stale state.
        if (changed.has("items")) this.#measure();
        this.#keepFocusVisible();
    }

    /**
     * The arrow you just spent fades to invisible; leaving focus on it takes the focus ring with it.
     * Hand focus to the opposite arrow, which is by definition still usable.
     */
    #keepFocusVisible() {
        const active = this.shadowRoot?.activeElement;
        if (!(active instanceof HTMLElement) || active.getAttribute("aria-disabled") !== "true") return;
        this.renderRoot.querySelector<HTMLElement>('button[aria-disabled="false"]')?.focus();
    }

    disconnectedCallback() {
        this.#resizeObserver?.disconnect();
        this.#resizeObserver = undefined;
        if (this.#rafHandle !== null) cancelAnimationFrame(this.#rafHandle);
        this.#rafHandle = null;
        super.disconnectedCallback();
    }

    get #track(): HTMLElement | null {
        return this.renderRoot.querySelector<HTMLElement>(".carousel-track");
    }

    #measure() {
        const track = this.#track;
        if (!track) return;
        const edges = edgeState(track.scrollLeft, track.clientWidth, track.scrollWidth);
        this.canPrev = edges.canPrev;
        this.canNext = edges.canNext;
    }

    /** Coalesce the high-frequency scroll event to one measurement per animation frame. */
    #onScroll() {
        if (this.#rafHandle !== null) return;
        this.#rafHandle = requestAnimationFrame(() => {
            this.#rafHandle = null;
            this.#measure();
        });
    }

    #page(direction: 1 | -1) {
        const track = this.#track;
        if (!track) return;
        // Measure the gap-aware distance between two card starts rather than using the track width —
        // the latter drifts by one gap per card on a long rail.
        const first = track.children[0] as HTMLElement | undefined;
        const second = track.children[1] as HTMLElement | undefined;
        const stride = slideStride(first?.offsetLeft ?? 0, second?.offsetLeft ?? 0, track.clientWidth);
        const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
        track.scrollBy({
            left: direction * pageDistance(stride, track.clientWidth),
            behavior,
        });
    }

    /** Left/Right page the rail while an arrow is focused; a button alone only takes Enter/Space. */
    #onArrowKey(event: KeyboardEvent) {
        const direction = arrowKeyAction(event.key, this.canPrev, this.canNext);
        if (direction === null) return;
        // Only swallow the key when it actually paged, so an inert arrow key still scrolls the page.
        event.preventDefault();
        this.#page(direction);
    }

    // `label` is passed in rather than resolved here: Lingui extracts by static analysis, so each
    // `i18n.t({ id, message })` has to appear as a literal call, not behind a ternary.
    // The icon needs `block` + an explicit box: w-icon is inline by default, so its line-box leaves
    // the glyph off-centre in the circle even inside a flex centring context.
    #renderArrow(direction: 1 | -1, enabled: boolean, label: string) {
        return html`<button
            type="button"
            aria-label="${label}"
            aria-disabled="${!enabled}"
            tabindex="${enabled ? nothing : "-1"}"
            @click="${() => enabled && this.#page(direction)}"
            @keydown="${this.#onArrowKey}"
            class="${ARROW_BASE} ${enabled ? "pointer-events-auto" : ARROW_DISABLED}"
        >
            <w-icon
                name="${direction === -1 ? "ChevronLeft" : "ChevronRight"}"
                size="24px"
                locale="sv"
                class="block w-24 h-24 s-icon"
                aria-hidden="true"
            ></w-icon>
        </button>`;
    }

    render() {
        // Driven by the measurement, not the item count: five cards on a wide desktop overflow
        // nothing, and guessing from the count would leave an empty focus stop on the track plus
        // two invisible buttons in the DOM. Both flags are false until the track has been measured,
        // so the controls appear only once there is genuinely somewhere to scroll.
        const scrollable = this.canPrev || this.canNext;
        // min-w-0 on the wrapper is load-bearing: as a grid item it defaults to min-width:auto and
        // would stretch to the rail's full content width instead of letting the track scroll. When
        // the track is a focus stop it also needs its own role and name, or a screen reader
        // announces an unlabelled stop — reusing the heading, per the WAI carousel pattern.
        const previousLabel = i18n.t({
            id: "market-frontpage.latest-models.previous",
            message: "Previous",
        });
        const nextLabel = i18n.t({
            id: "market-frontpage.latest-models.next",
            message: "Next",
        });

        return html`<section class="grid gap-16 max-w-full" aria-roledescription="carousel" aria-labelledby="${HEADING_ID}">
            <h3 class="t3 mb-0" id="${HEADING_ID}">${this.header}</h3>
            <div class="relative -m-12 min-w-0">
                <div
                    class="carousel-track flex gap-16 overflow-x-auto overscroll-x-contain p-12 focusable"
                    role="${scrollable ? "group" : nothing}"
                    aria-labelledby="${scrollable ? HEADING_ID : nothing}"
                    tabindex="${scrollable ? "0" : nothing}"
                    @scroll="${this.#onScroll}"
                >
                    ${this.items.map((item) => {
                        const textColor =
                            item.textColor === "light" ? "s-text-inverted-static" : item.textColor === "dark" ? "s-text-static" : "s-text";
                        const headingId = `newcar-latest-model-${item.id}`;

                        return html`<w-card class="max-w-[223] min-w-[223]">
                            <article class="flex flex-col p-16 pb-24 ${textColor}" style="background-color:${item.backgroundColor}">
                                <a
                                    href="${item.href}"
                                    aria-owns="${headingId}"
                                    class="absolute inset-0 rounded-8 focusable focusable-inset"
                                    @click="${() => item.trackingEvent && trackClickEvent(item.trackingEvent)}"
                                ></a>
                                <h4 class="t4 mb-4" id="${headingId}">${item.name}</h4>
                                <p class="text-caption mb-24">${item.displayPrice}</p>
                                <img
                                    src="${item.image}"
                                    class="mx-auto w-91/100 card-image"
                                    loading="lazy"
                                    width="800"
                                    height="400"
                                    alt="${item.name}"
                                />
                            </article>
                        </w-card>`;
                    })}
                </div>
                ${
                    scrollable
                        ? html`<div class="pointer-events-none absolute inset-0 z-10 hidden sm:flex items-center justify-between px-8">
                              ${this.#renderArrow(-1, this.canPrev, previousLabel)} ${this.#renderArrow(1, this.canNext, nextLabel)}
                          </div>`
                        : nothing
                }
            </div>
        </section>`;
    }
}

customElements.define("new-car-latest-models", NewCarLatestModels as unknown as CustomElementConstructor);
